/**
 * SOLID Review
 * S — Good. One concern: the commands, server answers and DTOs of the session decisions — entering the
 *     game screen, identity, seating at a table, the entry funnel.
 * O — Good. A new session move is a request, an answer and a command here; existing ones do not change.
 * L — N/A — no inheritance hierarchy.
 * I — Good. Each schema is one decision's input or output, named for it.
 * D — Good. zod, the shared primitives and the session and table schemas.
 */
import { z } from 'zod';
import { Identifier } from './primitives';
import { ClientResume, ClientStored, ClientScreen } from './ClientSession';
import { PayloadActiveRound } from './TableEvents';
import { PayloadTokenAccount } from './AuthMoves';

/**
 * THE SESSION'S MOVES (frontend strict-ports plan, FE2, 2026-10-03). The same shape as the table's
 * (`TableMoves.ts`): a PLAN decision says what to ask the server, the Application asks outside its
 * serial section, and an ANSWER decision applies what came back to the session and table as they are by
 * then. The server's answers are typed to @ml3d/contract's GraphQL nullability — `Room`, `RejoinResult`,
 * `RoomCodeLookup`, `ActiveRoomForPlayer`, `Player` — and the REST `MeResponse`, as much of each as a
 * decision reads.
 */

// ── what the server answers ────────────────────────────────────────────────────────────────────

/** A table as `createRoom`, `joinRoom` and `lateJoinRoom` return it (`Room`). */
export const PayloadSeatRoom = z.object({
  id: Identifier,
  roomCode: z.string(),
  playerIds: z.array(z.string()),
  players: z.array(z.object({ id: z.string(), displayName: z.string() })),
  status: z.string(),
}).meta({ id: 'PayloadSeatRoom' });

/** A suspended seat re-taken by code (`RejoinResult`): the seat's own player id may differ from the tab's. */
export const PayloadRejoin = z.object({
  playerId: Identifier,
  roomId: Identifier,
  roomCode: z.string(),
  roomStatus: z.string(),
  players: z.array(z.object({ id: z.string(), displayName: z.string() })),
}).meta({ id: 'PayloadRejoin' });

/** `getRoomByCode` as the signed-in client selects it: the room's state and every member, connected or not. */
export const PayloadRoomCodeLookup = z.object({
  roomId: Identifier,
  roomStatus: z.string(),
  playerCount: z.number().int(),
  suspendedPlayers: z.array(z.object({
    id: z.string(),
    displayName: z.string(),
    email: z.string().nullish(),
    isConnected: z.boolean(),
  })),
}).meta({ id: 'RoomCodeLookup' });

/** The live room the server still seats this player in (`ActiveRoomForPlayer`) — the cold login's question. */
export const PayloadActiveRoomForPlayer = z.object({
  roomId: Identifier,
  roomCode: z.string(),
  roomStatus: z.string(),
  playerCount: z.number().int(),
}).meta({ id: 'PayloadActiveRoomForPlayer' });

/** The signed-in player's own profile (`MeResponse`), as much as a seat needs. */
export const PayloadMe = z.object({
  id: Identifier,
  displayName: z.string(),
  /** The account's address (FE3: the profile reads it from the same answer). */
  email: z.string().nullable(),
  matchCredits: z.number().int(),
  isAdmin: z.boolean(),
}).meta({ id: 'PayloadMe' });

/** A guest player the server just created (`Player`). */
export const PayloadCreatedPlayer = z.object({
  id: Identifier,
  displayName: z.string(),
  matchCredits: z.number().int(),
}).meta({ id: 'PayloadCreatedPlayer' });

// ── what React asks for ────────────────────────────────────────────────────────────────────────

/** The page loaded: what its URL asked for (`?join=<code>`, or the return from Stripe's checkout). */
export const CommandStartSession = z.object({
  joinCode: z.string().nullable(),
  paymentReturn: z.boolean(),
}).meta({ id: 'CommandStartSession' });
export const CommandCreateGuest = z.object({ displayName: z.string() }).meta({ id: 'CommandCreateGuest' });
export const CommandTableCode = z.object({ code: z.string() }).meta({ id: 'CommandTableCode' });
export const CommandNavigate = z.object({ screen: ClientScreen }).meta({ id: 'CommandNavigate' });

// ── requests the session sends ─────────────────────────────────────────────────────────────────

/** A seat by room id, for a player: a join, a restore, the round read after a seat. */
export const RequestSeat = z.object({ roomId: Identifier, playerId: Identifier }).meta({ id: 'RequestSeat' });
export const RequestRejoin = z.object({
  roomCode: z.string(),
  playerId: Identifier,
  displayName: z.string(),
  email: z.string().nullable(),
}).meta({ id: 'RequestRejoin' });
export const RequestLateJoin = z.object({
  roomId: Identifier,
  playerId: Identifier,
  /** The nickname, de-duplicated against the table ("Bob*"). */
  displayName: z.string(),
  matchCredits: z.number().int(),
}).meta({ id: 'RequestLateJoin' });
export const RequestHost = z.object({ hostPlayerId: Identifier }).meta({ id: 'RequestHost' });
export const RequestPlayer = z.object({ playerId: Identifier }).meta({ id: 'RequestPlayer' });
export const RequestGuest = z.object({ displayName: z.string() }).meta({ id: 'RequestGuest' });
/** A table code to look up; null when there is none to look up. */
export const RequestCodeLookup = z.object({ code: z.string().nullable() }).meta({ id: 'RequestCodeLookup' });

// ── the decisions' commands ────────────────────────────────────────────────────────────────────

export const CommandSessionStarted = z.object({
  intent: CommandStartSession,
  stored: ClientStored,
  /** The account a stored, still-valid token belongs to (FE3: `hydrate`'s JWT half, read through ClientAuthServer). */
  token: PayloadTokenAccount.nullable(),
}).meta({ id: 'CommandSessionStarted' });
export const CommandPlanEntry = z.object({ stored: ClientStored }).meta({ id: 'CommandPlanEntry' });
/** `me` answered — null when there is no signed-in player behind the stored token. */
export const CommandMeAnswered = z.object({ me: PayloadMe.nullable() }).meta({ id: 'CommandMeAnswered' });
/** The stored game's room, looked up by its code — null when it is gone or the lookup failed. */
export const CommandResumeValidated = z.object({
  record: ClientResume,
  lookup: z.object({ roomStatus: z.string() }).nullable(),
}).meta({ id: 'CommandResumeValidated' });
export const CommandPlanRestore = z.object({ record: ClientResume }).meta({ id: 'CommandPlanRestore' });
/** The restore's join answered — null when the room no longer takes this player. */
export const CommandRestoreJoined = z.object({ record: ClientResume, room: PayloadSeatRoom.nullable() }).meta({ id: 'CommandRestoreJoined' });
/** The live round after a seat was taken mid-game — null between rounds, or when the read failed. */
export const CommandRoundRestored = z.object({ request: RequestSeat, activeRound: PayloadActiveRound.nullable() }).meta({ id: 'CommandRoundRestored' });
export const CommandPlanIntent = z.object({
  /** The cold login's lookup already ran in this entry and found nothing. */
  coldChecked: z.boolean(),
}).meta({ id: 'CommandPlanIntent' });
/** The pending code's room — null when no room has it; `failed` when the lookup itself failed. */
export const CommandJoinRouted = z.object({
  code: z.string(),
  lookup: PayloadRoomCodeLookup.nullable(),
  failed: z.boolean(),
}).meta({ id: 'CommandJoinRouted' });
export const CommandTableJoined = z.object({ request: RequestSeat, room: PayloadSeatRoom.nullable() }).meta({ id: 'CommandTableJoined' });
export const CommandTableRejoined = z.object({ request: RequestRejoin, rejoin: PayloadRejoin.nullable() }).meta({ id: 'CommandTableRejoined' });
export const CommandTableLateJoined = z.object({ request: RequestLateJoin, room: PayloadSeatRoom.nullable() }).meta({ id: 'CommandTableLateJoined' });
/** The auto-host's table — or, when the server refused, its own message; neither: the failure was ours. */
export const CommandTableHosted = z.object({
  request: RequestHost,
  room: PayloadSeatRoom.nullable(),
  serverMessage: z.string().nullable(),
}).meta({ id: 'CommandTableHosted' });
export const CommandActiveRoomFound = z.object({ request: RequestPlayer, room: PayloadActiveRoomForPlayer.nullable() }).meta({ id: 'CommandActiveRoomFound' });
export const CommandGuestCreated = z.object({ request: RequestGuest, player: PayloadCreatedPlayer }).meta({ id: 'CommandGuestCreated' });
export const CommandTableCodeChecked = z.object({ code: z.string(), found: z.boolean() }).meta({ id: 'CommandTableCodeChecked' });

// ── what the decisions answer the Application ─────────────────────────────────────────────────

/**
 * How an entry into the game screen proceeds. `identity`: known (a player, or a stored guest seated just
 * now), `me` (ask the server who the token belongs to), or `name` (an anonymous visitor names themselves
 * first). `resume`: nothing stored, a stored game to trust as it is, or one to check by its code first.
 */
export const DtoEntryPlan = z.object({
  identity: z.enum(['known', 'me', 'name']),
  resume: z.enum(['none', 'trust', 'validate']),
  record: ClientResume.nullable(),
}).meta({ id: 'DtoEntryPlan' });
/** Whether to go on: the restore after a validation, the round read after a seat. */
export const DtoProceed = z.object({ proceed: z.boolean() }).meta({ id: 'DtoProceed' });
/** `me` found no player: the stored token is worthless, and the tab signs out. */
export const DtoSignOut = z.object({ signOut: z.boolean() }).meta({ id: 'DtoSignOut' });
/** What an entry does once identity and any stored game have settled. */
export const DtoIntent = z.object({ next: z.enum(['seated', 'join', 'cold', 'host', 'idle', 'wait']) }).meta({ id: 'DtoIntent' });
/** The smart join's route (SmartJoinByCodeUseCase's), or `stop` when it ended with an error. */
export const DtoJoinRoute = z.object({
  route: z.discriminatedUnion('type', [
    z.object({ type: z.literal('JOIN'), request: RequestSeat }),
    z.object({ type: z.literal('REJOIN'), request: RequestRejoin }),
    z.object({ type: z.literal('LATE_JOIN'), request: RequestLateJoin }),
    z.object({ type: z.literal('stop') }),
  ]),
}).meta({ id: 'DtoJoinRoute' });
/** A seat taken mid-game reads the live round next; null when the seat needs no round. */
export const DtoRoundRead = z.object({ read: RequestSeat.nullable() }).meta({ id: 'DtoRoundRead' });
/** The cold login found a live room: the record to restore it from. */
export const DtoColdRoom = z.object({ record: ClientResume.nullable() }).meta({ id: 'DtoColdRoom' });

export type IPayloadSeatRoom = z.infer<typeof PayloadSeatRoom>;
export type IPayloadRejoin = z.infer<typeof PayloadRejoin>;
export type IPayloadRoomCodeLookup = z.infer<typeof PayloadRoomCodeLookup>;
export type IPayloadMe = z.infer<typeof PayloadMe>;
export type ICommandStartSession = z.infer<typeof CommandStartSession>;
export type ICommandCreateGuest = z.infer<typeof CommandCreateGuest>;
export type ICommandTableCode = z.infer<typeof CommandTableCode>;
export type ICommandNavigate = z.infer<typeof CommandNavigate>;
export type IPayloadSeat = z.infer<typeof RequestSeat>;
export type IPayloadRejoinRequest = z.infer<typeof RequestRejoin>;
export type IPayloadLateJoin = z.infer<typeof RequestLateJoin>;
export type IPayloadHost = z.infer<typeof RequestHost>;
export type IPayloadPlayer = z.infer<typeof RequestPlayer>;
export type IPayloadGuest = z.infer<typeof RequestGuest>;
export type IPayloadCodeLookup = z.infer<typeof RequestCodeLookup>;
export type ICommandSessionStarted = z.infer<typeof CommandSessionStarted>;
export type ICommandPlanEntry = z.infer<typeof CommandPlanEntry>;
export type ICommandMeAnswered = z.infer<typeof CommandMeAnswered>;
export type ICommandResumeValidated = z.infer<typeof CommandResumeValidated>;
export type ICommandPlanRestore = z.infer<typeof CommandPlanRestore>;
export type ICommandRestoreJoined = z.infer<typeof CommandRestoreJoined>;
export type ICommandRoundRestored = z.infer<typeof CommandRoundRestored>;
export type ICommandPlanIntent = z.infer<typeof CommandPlanIntent>;
export type ICommandJoinRouted = z.infer<typeof CommandJoinRouted>;
export type ICommandTableJoined = z.infer<typeof CommandTableJoined>;
export type ICommandTableRejoined = z.infer<typeof CommandTableRejoined>;
export type ICommandTableLateJoined = z.infer<typeof CommandTableLateJoined>;
export type ICommandTableHosted = z.infer<typeof CommandTableHosted>;
export type ICommandActiveRoomFound = z.infer<typeof CommandActiveRoomFound>;
export type ICommandGuestCreated = z.infer<typeof CommandGuestCreated>;
export type ICommandTableCodeChecked = z.infer<typeof CommandTableCodeChecked>;
export type IEntityEntryPlan = z.infer<typeof DtoEntryPlan>;
export type IEntityProceed = z.infer<typeof DtoProceed>;
export type IEntitySignOut = z.infer<typeof DtoSignOut>;
export type IEntityIntent = z.infer<typeof DtoIntent>;
export type IEntityJoinRoute = z.infer<typeof DtoJoinRoute>;
export type IEntityRoundRead = z.infer<typeof DtoRoundRead>;
export type IEntityColdRoom = z.infer<typeof DtoColdRoom>;
